import {
  cycleCountIdFormat,
  cycleCountPlanIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import { storageLocationIdFormat } from "@redotech/redo-warehouse-id";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import { cycleCountTargetTypeSchema } from "./list-cycle-counts";

/**
 * Counting one SKU across its bins is a cycle count of a single product, so the
 * walk survives a refresh and only that SKU's levels are touched.
 */
export const startSkuCycleCount = unaryRpc({
  name: "startSkuCycleCount" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    productId: productIdFormat.schema,
  }),
  output: z.object({ cycleCountId: cycleCountIdFormat.schema }),
});

const WalkBin = z.object({
  storageLocationId: storageLocationIdFormat.schema,
  binName: z.string(),
  /** The plan row this bin is counted against. */
  planId: cycleCountPlanIdFormat.schema,
  /** Set once this bin has been counted in this walk. */
  countedAt: z.date().nullable(),
  /** A bin plan row the counter attested holds nothing. */
  confirmedEmpty: z.boolean(),
  lotNumber: z.string().nullable(),
  expirationDate: z.date().nullable(),
});

/**
 * One planned product and the bins its stock sits in. The walk is two levels
 * deep on an item count: pick the product, then count it bin by bin.
 */
const WalkProduct = z.object({
  planId: cycleCountPlanIdFormat.schema,
  productId: productIdFormat.schema,
  title: z.string().nullable(),
  sku: z.string().nullable(),
  imageUrl: z.string().nullable(),
  trackLotFulfillment: z.boolean(),
  /** Set once this product's plan row is satisfied. */
  finishedAt: z.date().nullable(),
  bins: z.array(WalkBin),
});

/** The warehouse view of a cycle count: bins to walk, blind of expectations. */
export const getCycleCountWalk = unaryRpc({
  name: "getCycleCountWalk" as const,
  input: z.object({ cycleCountId: cycleCountIdFormat.schema }),
  output: z.object({
    cycleCountId: cycleCountIdFormat.schema,
    locationId: locationIdFormat.schema,
    /** Bins mean every product in them counts; items mean only those products. */
    targetType: cycleCountTargetTypeSchema,
    /** Populated on a bin count; an item count walks `products` instead. */
    bins: z.array(WalkBin),
    products: z.array(WalkProduct),
    /** Plan rows: products on an item count, bins on a bin count. */
    plannedTotal: z.int(),
    plannedFinished: z.int(),
    completedAt: z.date().nullable(),
    createdAt: z.date(),
  }),
});
